import React from "react";
import { Button, CircularProgress, useMediaQuery } from "@material-ui/core";
import PositionedTooltips from "./utilities/HoverToolTip";
import theme from "../../themes";

// Common styles for activation forms
export const activationFormStyles = {
  // Container styles
  outerContainer: {
    maxWidth: "900px",
    margin: "0 auto",
    border: "2px dotted #CCC",
    borderRadius: "12px",
    padding: "30px",
    backgroundColor: "transparent",
  },

  innerContainer: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "40px",
  },

  // Typography styles
  infoText: {
    fontFamily: "Inter, sans-serif",
    fontSize: "18px",
    lineHeight: "28px",
    fontWeight: 400,
    color: "#000000",
    marginBottom: "12px",
  },

  label: {
    fontFamily: "Inter, sans-serif",
    fontSize: "18px",
    fontWeight: 600,
    color: "#000000",
  },

  descriptionText: {
    fontFamily: "Inter, sans-serif",
    fontSize: "16px",
    fontWeight: 400,
    color: "#000000",
    margin: "0 0 12px 0",
  },

  // Input field styles
  inputField: {
    fontFamily: "Inter, sans-serif",
    fontSize: "16px",
    height: "56px",
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    color: "#333333",
  },

  inputSx: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#CCCCCC",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#CCCCCC",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#000000",
      },
    },
  },

  // Annual fee styles
  annualFeeLabel: {
    fontFamily: "Inter, sans-serif",
    fontSize: "30px",
    fontWeight: 400,
    color: "#000000",
  },

  annualFeeAmount: {
    fontFamily: "Inter, sans-serif",
    fontSize: "30px",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },

  // Button styles
  activateButton: {
    fontFamily: "Inter, sans-serif",
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontWeight: 700,
    borderRadius: "8px",
    padding: "12px 40px",
    textTransform: "none" as const,
    fontSize: "20px",
    minWidth: "200px",
    boxShadow: "none",
  },

  clearButton: {
    fontFamily: "Inter, sans-serif",
    backgroundColor: "#000000",
    color: "#FFFFFF",
    fontWeight: 700,
    borderRadius: "8px",
    padding: "12px 40px",
    textTransform: "none" as const,
    fontSize: "20px",
    minWidth: "200px",
    boxShadow: "none",
  },
};

// Reusable Info Icon SVG
export const InfoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 9H13V7H11M12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM12 2C10.6868 2 9.38642 2.25866 8.17317 2.7612C6.95991 3.26375 5.85752 4.00035 4.92893 4.92893C3.05357 6.8043 2 9.34784 2 12C2 14.6522 3.05357 17.1957 4.92893 19.0711C5.85752 19.9997 6.95991 20.7362 8.17317 21.2388C9.38642 21.7413 10.6868 22 12 22C14.6522 22 17.1957 20.9464 19.0711 19.0711C20.9464 17.1957 22 14.6522 22 12C22 10.6868 21.7413 9.38642 21.2388 8.17317C20.7362 6.95991 19.9997 5.85752 19.0711 4.92893C18.1425 4.00035 17.0401 3.26375 15.8268 2.7612C14.6136 2.25866 13.3132 2 12 2ZM11 17H13V11H11V17Z" fill="#888888" />
  </svg>
);

// Reusable Form Label Component
interface FormLabelProps {
  label: string;
  required?: boolean;
  showInfoIcon?: boolean;
  tooltipContent?: string;
}

export const FormLabel: React.FC<FormLabelProps> = ({ label, required = false, showInfoIcon = true, tooltipContent }) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <label style={{ ...activationFormStyles.label, ...(isMobile ? { fontSize: "15px" } : {}) }}>
        {label}{required && <span style={{ color: "red" }}>*</span>}
      </label>
      {showInfoIcon && (
        <PositionedTooltips
          showIt={true}
          description={tooltipContent || "This field allows you to select the relevant option for the activation process. Hover over any info icon for more details about that specific field."}
          disablePadding
        />
      )}
    </div>
  );
};

// Reusable Form Description — matches InfoTextSection mobile sizing (14/20) for visual consistency
interface FormDescriptionProps {
  children: React.ReactNode;
}

export const FormDescription: React.FC<FormDescriptionProps> = ({ children }) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  return (
    <p
      style={{
        ...activationFormStyles.descriptionText,
        ...(isMobile ? { fontSize: "14px", lineHeight: "20px", margin: "0 0 10px 0" } : {}),
      }}
    >
      {children}
    </p>
  );
};

// Reusable Info Text Section
interface InfoTextSectionProps {
  texts: string[];
}

export const InfoTextSection: React.FC<InfoTextSectionProps> = ({ texts }) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const mobileOverride = isMobile ? { fontSize: "14px", lineHeight: "20px", marginBottom: "10px" } : {};
  return (
    <div style={{ marginBottom: isMobile ? "20px" : "32px" }}>
      {texts.map((text, index) => (
        <p key={index} style={{ ...activationFormStyles.infoText, ...mobileOverride, textAlign: "left", margin: index === texts.length - 1 ? "0" : "0 0 12px 0" }}>
          {text}
        </p>
      ))}
    </div>
  );
};

// Reusable Annual Fee Display
interface AnnualFeeDisplayProps {
  amount: number;
  label?: string;
  showPerYear?: boolean;
}

export const AnnualFeeDisplay: React.FC<AnnualFeeDisplayProps> = ({ amount, label = "Annual Fee", showPerYear = false }) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const mobileOverride = isMobile ? { fontSize: "16px" } : {};
  return (
    <div style={{ textAlign: "center", marginBottom: isMobile ? "16px" : "24px" }}>
      <span style={{ ...activationFormStyles.annualFeeLabel, ...mobileOverride }}>
        {label}{" "}
      </span>
      <span style={{ ...activationFormStyles.annualFeeAmount, ...mobileOverride }}>
        ${amount}{showPerYear ? "/year" : ""}
      </span>
    </div>
  );
};

// Reusable Action Buttons
interface ActionButtonsProps {
  onActivate: () => void;
  onClearAll: () => void;
  isActivating?: boolean;
  activateDisabled?: boolean;
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({
  onActivate,
  onClearAll,
  isActivating = false,
  activateDisabled = false,
}) => (
  <div style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap" }}>
    <Button
      variant="contained"
      onClick={onActivate}
      disabled={isActivating || activateDisabled}
      style={{ ...activationFormStyles.activateButton, maxWidth: "100%" }}
    >
      {isActivating ? <CircularProgress size={24} color="inherit" /> : "ACTIVATE MEMBER"}
    </Button>
    <Button
      variant="contained"
      onClick={onClearAll}
      style={{ ...activationFormStyles.clearButton, maxWidth: "100%" }}
      disabled={isActivating}
    >
      RESET
    </Button>
  </div>
);

// Reusable Form Container
interface FormContainerProps {
  children: React.ReactNode;
  maxWidth?: string;
}

export const FormContainer: React.FC<FormContainerProps> = ({ children, maxWidth = "900px" }) => (
  <div style={{ ...activationFormStyles.outerContainer, maxWidth, boxSizing: "border-box", padding: "clamp(12px, 3vw, 30px)" }}>
    <div style={{ ...activationFormStyles.innerContainer, padding: "clamp(16px, 4vw, 40px)", boxSizing: "border-box" }}>
      {children}
    </div>
  </div>
);
